/**
 * Arrow geometry in canvas coordinates: where an arrow starts and ends, the
 * path it draws, its arrowhead angles and the point halfway along it. Shared
 * by the renderer and the arrow toolbar.
 */
import type { Arrow, ConnectorPosition, ArrowStyle, ConnectionType } from '@/stores/arrowStore';
import type { Item } from '@/stores/canvasStore';

// Calculate connector point position on an item
export function getConnectorPoint(
  item: Item,
  position: ConnectorPosition
): { x: number; y: number } {
  const { position_x: x, position_y: y, width, height } = item;
  
  switch (position) {
    case 'top':
      return { x: x + width / 2, y: y };
    case 'bottom':
      return { x: x + width / 2, y: y + height };
    case 'left':
      return { x: x, y: y + height / 2 };
    case 'right':
      return { x: x + width, y: y + height / 2 };
    default:
      return { x: x + width / 2, y: y + height / 2 };
  }
}

// Calculate lateral offset for fan-out when multiple arrows share a connector
function getFanOffset(
  index: number,
  count: number,
  connector: ConnectorPosition | null
): { dx: number; dy: number } {
  if (count <= 1 || !connector) return { dx: 0, dy: 0 };

  // Total spread width scales with count, capped to prevent exceeding item edges
  const SPREAD = Math.min(40, count * 12);
  // Offset from center: evenly distributed from -SPREAD/2 to +SPREAD/2
  const offset = count === 1 ? 0 : ((index - (count - 1) / 2) / (count - 1)) * SPREAD;

  // Offset perpendicular to connector's exit direction
  switch (connector) {
    case 'right':
    case 'left':
      return { dx: 0, dy: offset }; // spread vertically along the edge
    case 'top':
    case 'bottom':
      return { dx: offset, dy: 0 }; // spread horizontally along the edge
    default:
      return { dx: 0, dy: 0 };
  }
}

// Calculate control points for bezier curve
function calculateControlPoints(
  start: { x: number; y: number },
  end: { x: number; y: number },
  startConnector: ConnectorPosition | null,
  endConnector: ConnectorPosition | null
) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const offset = Math.min(distance * 0.4, 100);
  
  let cp1 = { x: start.x, y: start.y };
  let cp2 = { x: end.x, y: end.y };
  
  // Adjust control points based on connector positions
  if (startConnector) {
    switch (startConnector) {
      case 'top':
        cp1 = { x: start.x, y: start.y - offset };
        break;
      case 'bottom':
        cp1 = { x: start.x, y: start.y + offset };
        break;
      case 'left':
        cp1 = { x: start.x - offset, y: start.y };
        break;
      case 'right':
        cp1 = { x: start.x + offset, y: start.y };
        break;
    }
  } else {
    // Free-floating start - use direction
    cp1 = { x: start.x + dx * 0.3, y: start.y + dy * 0.3 };
  }
  
  if (endConnector) {
    switch (endConnector) {
      case 'top':
        cp2 = { x: end.x, y: end.y - offset };
        break;
      case 'bottom':
        cp2 = { x: end.x, y: end.y + offset };
        break;
      case 'left':
        cp2 = { x: end.x - offset, y: end.y };
        break;
      case 'right':
        cp2 = { x: end.x + offset, y: end.y };
        break;
    }
  } else {
    // Free-floating end - use direction
    cp2 = { x: end.x - dx * 0.3, y: end.y - dy * 0.3 };
  }
  
  return { cp1, cp2 };
}

type Point = { x: number; y: number };

// Corner points of a 'step' (right-angle) path, start to end
function stepPoints(
  start: Point,
  end: Point,
  startConnector: ConnectorPosition | null,
  endConnector: ConnectorPosition | null
): Point[] {
  const isHorizontalStart = startConnector === 'left' || startConnector === 'right';
  const isHorizontalEnd = endConnector === 'left' || endConnector === 'right';

  if (isHorizontalStart && isHorizontalEnd) {
    // Both horizontal: horizontal, vertical, horizontal
    const midX = (start.x + end.x) / 2;
    return [start, { x: midX, y: start.y }, { x: midX, y: end.y }, end];
  }
  if (!isHorizontalStart && !isHorizontalEnd) {
    // Both vertical: vertical, horizontal, vertical
    const midY = (start.y + end.y) / 2;
    return [start, { x: start.x, y: midY }, { x: end.x, y: midY }, end];
  }
  if (isHorizontalStart) {
    // Start horizontal, end vertical
    return [start, { x: end.x, y: start.y }, end];
  }
  // Start vertical, end horizontal
  return [start, { x: start.x, y: end.y }, end];
}

// Calculate angle for arrowhead rotation
function calculateAngle(fromX: number, fromY: number, toX: number, toY: number): number {
  return Math.atan2(toY - fromY, toX - fromX) * (180 / Math.PI);
}

export interface ArrowSiblingInfo {
  startIndex: number;
  startCount: number;
  endIndex: number;
  endCount: number;
}

export interface ArrowGeometry {
  start: Point;
  end: Point;
  path: string;
  startAngle: number;
  endAngle: number;
  /** A point on the drawn line halfway along it: where the label sits */
  midpoint: Point;
  /** Highest and lowest y the drawn line reaches, so overlays can clear it */
  top: number;
  bottom: number;
}

function yRange(points: Point[]): { top: number; bottom: number } {
  let top = Infinity;
  let bottom = -Infinity;
  for (const p of points) {
    if (p.y < top) top = p.y;
    if (p.y > bottom) bottom = p.y;
  }
  return { top, bottom };
}

/**
 * The drawn shape of an arrow in canvas coordinates. Shared by the renderer
 * and the arrow toolbar, so the toolbar sits on the line the user sees.
 */
export function computeArrowGeometry(
  arrow: Arrow,
  startItem: Item | null | undefined,
  endItem: Item | null | undefined,
  siblingInfo?: ArrowSiblingInfo
): ArrowGeometry | null {
  let start: Point;
  if (arrow.start_item_id && arrow.start_connector && startItem) {
    start = getConnectorPoint(startItem, arrow.start_connector);
    // Fan out sibling arrows that share this connector
    if (siblingInfo && siblingInfo.startCount > 1) {
      const fan = getFanOffset(siblingInfo.startIndex, siblingInfo.startCount, arrow.start_connector);
      start = { x: start.x + fan.dx, y: start.y + fan.dy };
    }
  } else if (arrow.start_x !== null && arrow.start_y !== null) {
    start = { x: arrow.start_x, y: arrow.start_y };
  } else {
    return null;
  }

  let end: Point;
  if (arrow.end_item_id && arrow.end_connector && endItem) {
    end = getConnectorPoint(endItem, arrow.end_connector);
    if (siblingInfo && siblingInfo.endCount > 1) {
      const fan = getFanOffset(siblingInfo.endIndex, siblingInfo.endCount, arrow.end_connector);
      end = { x: end.x + fan.dx, y: end.y + fan.dy };
    }
  } else if (arrow.end_x !== null && arrow.end_y !== null) {
    end = { x: arrow.end_x, y: arrow.end_y };
  } else {
    return null;
  }

  const style: ArrowStyle = arrow.style || 'bezier';

  if (style === 'straight') {
    return {
      start,
      end,
      path: `M ${start.x} ${start.y} L ${end.x} ${end.y}`,
      startAngle: calculateAngle(end.x, end.y, start.x, start.y),
      endAngle: calculateAngle(start.x, start.y, end.x, end.y),
      midpoint: { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 },
      ...yRange([start, end]),
    };
  }

  if (style === 'step') {
    const pts = stepPoints(start, end, arrow.start_connector, arrow.end_connector);
    // Heads follow the first and last segments, not the straight line between the ends
    const first = pts.find((p) => p.x !== start.x || p.y !== start.y) ?? end;
    const last = [...pts].reverse().find((p) => p.x !== end.x || p.y !== end.y) ?? start;
    // Halfway: the middle of the middle segment, or the corner of an L
    const midpoint =
      pts.length === 4
        ? { x: (pts[1].x + pts[2].x) / 2, y: (pts[1].y + pts[2].y) / 2 }
        : pts[1];
    return {
      start,
      end,
      path: pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' '),
      startAngle: calculateAngle(first.x, first.y, start.x, start.y),
      endAngle: calculateAngle(last.x, last.y, end.x, end.y),
      midpoint,
      ...yRange(pts),
    };
  }

  // bezier (default)
  const { cp1, cp2 } = calculateControlPoints(start, end, arrow.start_connector, arrow.end_connector);
  const at = (t: number): Point => {
    const u = 1 - t;
    return {
      x: u * u * u * start.x + 3 * u * u * t * cp1.x + 3 * u * t * t * cp2.x + t * t * t * end.x,
      y: u * u * u * start.y + 3 * u * u * t * cp1.y + 3 * u * t * t * cp2.y + t * t * t * end.y,
    };
  };
  return {
    start,
    end,
    path: `M ${start.x} ${start.y} C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${end.x} ${end.y}`,
    startAngle: calculateAngle(cp1.x, cp1.y, start.x, start.y),
    endAngle: calculateAngle(cp2.x, cp2.y, end.x, end.y),
    midpoint: at(0.5),
    // Sampled: a curve can bow past its ends
    ...yRange(Array.from({ length: 17 }, (_, i) => at(i / 16))),
  };
}

/** The stroke colour new arrows get. Drawn in the theme's ink so it reads on light and dark paper. */
export const DEFAULT_ARROW_COLOR = '#374151';

/** The colour to paint an arrow with: user picks as stored, the untouched default in ink. */
export function arrowPaint(strokeColor: string | null | undefined): string {
  if (!strokeColor || strokeColor.toLowerCase() === DEFAULT_ARROW_COLOR) return 'hsl(var(--ink-2))';
  return strokeColor;
}

/** Connection types and the colour each paints the arrow. Partnr uses the same names. */
export const CONNECTION_TYPES: { value: ConnectionType; label: string; color: string }[] = [
  { value: 'default', label: 'Plain', color: DEFAULT_ARROW_COLOR },
  { value: 'causes', label: 'Causes', color: '#f97316' },
  { value: 'blocks', label: 'Blocks', color: '#ef4444' },
  { value: 'contains', label: 'Contains', color: '#8b5cf6' },
  { value: 'sequence', label: 'Leads to', color: '#3b82f6' },
  { value: 'related', label: 'Related', color: '#6b7280' },
];
